// Per-engine introspection-feed adapter for the identifier-index builder.
// PRD: Ralph/NavigationalFeatures/PRD.md Slice 17.
//
// `listParseableObjects` enumerates every view / inline-TVF / scalar SQL
// function / default-constraint / check-constraint body the engine exposes.
// The returned `PersistedObjectListing[]` carries each body along with the
// resolver `ParseOptions.engine` it should be parsed under, so Slices 18–21
// can hand each entry directly to `parseDocument` without re-deriving the
// dialect.
//
// `fetchObjectBody` provides a single-object refresh path used by Slice 22's
// DDL invalidator: when `ALTER VIEW`/`ALTER FUNCTION` fires, the index drops
// the old entries and re-pulls just that body via this function.
//
// Engine dispatch lives in per-engine files under `engines/` to keep this
// file under the 200-line budget.

import type { DatabaseEngineId } from '@/core/engines';

import type {
  ObjectKind,
  ParseableObjectKind,
  PersistedObjectListing,
  QualifiedName,
} from '../types';

import {
  listMssqlParseableObjects,
  fetchMssqlObjectBody,
  listMssqlProceduralObjects,
  fetchMssqlProceduralObjectBody,
  listMssqlSchemasHoldingObject,
} from './engines/mssqlObjectBodyFetcher';
import {
  listPgsqlParseableObjects,
  fetchPgsqlObjectBody,
  listPgsqlProceduralObjects,
  fetchPgsqlProceduralObjectBody,
  listPgsqlSchemasHoldingObject,
} from './engines/pgsqlObjectBodyFetcher';
import {
  listMysqlParseableObjects,
  fetchMysqlObjectBody,
  listMysqlProceduralObjects,
  fetchMysqlProceduralObjectBody,
  listMysqlSchemasHoldingObject,
} from './engines/mysqlObjectBodyFetcher';
import {
  listOracleParseableObjects,
  fetchOracleObjectBody,
  listOracleProceduralObjects,
  fetchOracleProceduralObjectBody,
  listOracleSchemasHoldingObject,
} from './engines/oracleObjectBodyFetcher';
import { catalogNameSpelling } from './engines/queryHelper';
import { listSqliteParseableObjects, fetchSqliteObjectBody } from './engines/sqliteObjectBodyFetcher';
import type { NavListingFetchOptions } from './engines/queryHelper';
import type { NavBodyBudget } from './navIndexBudget';

export type { PersistedObjectListing, ParseableObjectKind } from '../types';

export type FetcherOptions = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  /**
   * Shared body-byte allowance for one index build. Both feeds of one build
   * spend from the same tracker; when it is gone the per-engine fetchers stop
   * paging and `buildIndexForConnection` marks the index `partial`.
   */
  readonly bodyBudget?: NavBodyBudget;
  /**
   * `true` stops the feed where it stands — the connection lifecycle flips it
   * on teardown so a disconnect mid-build does not keep paging a catalog
   * nobody will read.
   */
  readonly shouldAbort?: () => boolean;
  /**
   * Fired when a feed's listing is not authoritative (a lost page, the row
   * ceiling, a truncated result — see `engines/navPagedRows.ts`). The
   * connection-wide build marks the index partial; the DDL invalidator's
   * constraint rebuild keeps its records.
   */
  readonly onIncomplete?: () => void;
  /**
   * The FAILED-QUERY subset of `onIncomplete` (a page and its un-paged
   * fallback, a later page, a feed's own catalog probe), as opposed to a cap.
   * `buildIndexForConnection` rejects on it — see `engines/navPagedRows.ts`.
   */
  readonly onFetchFailed?: () => void;
};

/** The subset of `FetcherOptions` the per-engine catalog feeds consume. */
function listingOptions(options: FetcherOptions): NavListingFetchOptions {
  return {
    connectionId: options.connectionId,
    database: options.database,
    budget: options.bodyBudget,
    shouldAbort: options.shouldAbort,
    onIncomplete: options.onIncomplete,
    onFetchFailed: options.onFetchFailed,
  };
}

export type FetchObjectBodyOptions = FetcherOptions & {
  readonly objectKind: ObjectKind;
  readonly objectName: QualifiedName;
};

/** True for object kinds the per-engine fetcher knows how to enumerate.
 *  Procedural kinds (`procedure` / `trigger`) flow through the sibling
 *  `listProceduralObjects` / `fetchProceduralObjectBody` dispatchers — see
 *  ProceduralBodyIndexing PRD PBI-2. */
export function isParseableObjectKind(kind: ObjectKind): kind is ParseableObjectKind {
  return (
    kind === 'view' ||
    kind === 'inline-tvf' ||
    kind === 'scalar-function' ||
    kind === 'procedure' ||
    kind === 'trigger' ||
    kind === 'default-constraint' ||
    kind === 'check-constraint'
  );
}

/**
 * Enumerate every parseable persisted object on the given connection +
 * database. Returns an empty array when the renderer's IPC bridge is not
 * available (test mode without the preload, mock connections without the
 * real driver).
 */
export async function listParseableObjects(
  options: FetcherOptions,
): Promise<readonly PersistedObjectListing[]> {
  const listing = listingOptions(options);
  switch (options.engineId) {
    case 'mssql':
      return listMssqlParseableObjects(listing);
    case 'pgsql':
      return listPgsqlParseableObjects(listing);
    case 'mysql':
      return listMysqlParseableObjects(listing);
    case 'oracle':
      return listOracleParseableObjects(listing);
    case 'sqlite':
      return listSqliteParseableObjects(listing);
    case 'mock':
    case 'kusto':
    case 'azurestorage':
      return [];
  }
}

/**
 * Re-pull a single object body. Returns `null` when the object is missing
 * from the catalog (already dropped, or never existed) so the index-builder
 * can emit a `parse-failed` placeholder. Procedural kinds (`procedure` /
 * `trigger`) are not handled here — the index-builder routes them through
 * `fetchProceduralObjectBody`.
 */
export async function fetchObjectBody(
  options: FetchObjectBodyOptions,
): Promise<string | null> {
  if (!isParseableObjectKind(options.objectKind)) return null;
  if (options.objectKind === 'procedure' || options.objectKind === 'trigger') return null;
  switch (options.engineId) {
    case 'mssql':
      return fetchMssqlObjectBody(options.connectionId, options.database, options.objectKind, options.objectName);
    case 'pgsql':
      return fetchPgsqlObjectBody(options.connectionId, options.database, options.objectKind, options.objectName);
    case 'mysql':
      return fetchMysqlObjectBody(options.connectionId, options.database, options.objectKind, options.objectName);
    case 'oracle':
      return fetchOracleObjectBody(options.connectionId, options.database, options.objectKind, options.objectName);
    case 'sqlite':
      return fetchSqliteObjectBody(options.connectionId, options.database, options.objectKind, options.objectName);
    case 'mock':
    case 'kusto':
    case 'azurestorage':
      return null;
  }
}

/**
 * Sibling feed to `listParseableObjects` for procedural bodies (procedures /
 * triggers). The index-builder merges both feeds in `buildIndexForConnection`
 * (ProceduralBodyIndexing PRD PBI-2). Per-engine adapters are live for
 * mssql/pgsql/mysql/oracle. SQLite has no STORED PROCEDURES; it does have triggers, but this index never fetches them (`sqliteObjectBodyFetcher.ts` selects `type = 'view'` and `type = 'table'` only), so it always
 * returns `[]`; `mock` has no real backing store so it also returns `[]`.
 */
export async function listProceduralObjects(
  options: FetcherOptions,
): Promise<readonly PersistedObjectListing[]> {
  const listing = listingOptions(options);
  switch (options.engineId) {
    case 'mssql':
      return listMssqlProceduralObjects(listing);
    case 'pgsql':
      return listPgsqlProceduralObjects(listing);
    case 'mysql':
      return listMysqlProceduralObjects(listing);
    case 'oracle':
      return listOracleProceduralObjects(listing);
    // SQLite has no stored procedures, and its triggers are NOT indexed:
    // `sqliteObjectBodyFetcher.ts` selects `type = 'view'` and
    // `type = 'table'` only, so no trigger body reaches the index. (An earlier
    // comment here claimed the parseable feed already returned them — it does
    // not, and its own query says so.) Nothing to page either way.
    case 'sqlite':
    case 'mock':
    case 'kusto':
    case 'azurestorage':
      return [];
  }
}

export type SchemasHoldingObjectOptions = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  /** The name as the statement spelled it. */
  readonly name: string;
  /** Whether the statement delimited it (`"…"`, `[…]`, backticks). */
  readonly quoted?: boolean;
  readonly kinds: readonly ObjectKind[];
};

/**
 * Every schema of the catalog that holds an object of `name` and one of
 * `kinds` — the DDL invalidator's answer to "where did this bare statement
 * run?". Folds the name to the engine's catalog spelling (unless quoted).
 * SQLite has one schema; `mock` / `kusto` / `azurestorage` hold nothing indexable. Rejects
 * when the catalog query fails (callers fall back to what they know).
 */
export async function listSchemasHoldingObject(
  options: SchemasHoldingObjectOptions,
): Promise<readonly string[]> {
  const name = catalogNameSpelling(options.engineId, options.name, options.quoted === true);
  switch (options.engineId) {
    case 'mssql':
      return listMssqlSchemasHoldingObject(options.connectionId, options.database, name, options.kinds);
    case 'pgsql':
      return listPgsqlSchemasHoldingObject(options.connectionId, options.database, name, options.kinds);
    case 'mysql':
      return listMysqlSchemasHoldingObject(options.connectionId, options.database, name, options.kinds);
    case 'oracle':
      return listOracleSchemasHoldingObject(options.connectionId, options.database, name, options.kinds);
    case 'sqlite':
      return ['main'];
    case 'mock':
    case 'kusto':
    case 'azurestorage':
      return [];
  }
}

/**
 * Re-pull a single procedural body (procedure / trigger). Mirrors
 * `fetchObjectBody`'s shape but is restricted to procedural kinds. Per-engine
 * adapters are live for mssql/pgsql/mysql/oracle; sqlite (no procedural
 * surface) and mock (no real backing store) always return `null`.
 */
export async function fetchProceduralObjectBody(
  options: FetchObjectBodyOptions,
): Promise<string | null> {
  if (options.objectKind !== 'procedure' && options.objectKind !== 'trigger') return null;
  switch (options.engineId) {
    case 'mssql':
      return fetchMssqlProceduralObjectBody(options.connectionId, options.database, options.objectKind, options.objectName);
    case 'pgsql':
      return fetchPgsqlProceduralObjectBody(options.connectionId, options.database, options.objectKind, options.objectName);
    case 'mysql':
      return fetchMysqlProceduralObjectBody(options.connectionId, options.database, options.objectKind, options.objectName);
    case 'oracle':
      return fetchOracleProceduralObjectBody(options.connectionId, options.database, options.objectKind, options.objectName);
    case 'sqlite':
    case 'mock':
    case 'kusto':
    case 'azurestorage':
      return null;
  }
}
